import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, AlertCircle, ChevronDown, ChevronUp, User, Phone, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PendingPayments() {
  const [customerPayments, setCustomerPayments] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/pending-payments/by-customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerPayments(response.data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedCustomer || paymentAmount <= 0) return;

    try {
      const token = localStorage.getItem('token');
      // Use phone-based payment endpoint to handle all sales including those without customer_id
      await axios.post(
        `${API}/collect-payment-by-phone?phone=${encodeURIComponent(selectedCustomer.customer_phone)}&amount=${paymentAmount}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payment of ₹${paymentAmount.toLocaleString()} collected successfully`);
      setShowPaymentDialog(false);
      setPaymentAmount(0);
      setSelectedCustomer(null);
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to collect payment');
    }
  };

  const totalPending = customerPayments.reduce((acc, c) => acc + c.balance_due, 0);
  const totalCustomers = customerPayments.length;

  const toggleExpand = (customerId) => {
    setExpandedCustomer(expandedCustomer === customerId ? null : customerId);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <p className="font-inter text-concrete-600">Loading pending payments...</p>
    </div>;
  }

  return (
    <div data-testid="pending-payments-page" className="space-y-8">
      <div>
        <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="pending-payments-title">
          <AlertCircle className="inline-block mr-3 h-10 w-10 text-brick-600" />
          Pending Payments
        </h1>
        <p className="font-inter text-concrete-600 mt-2">Track and collect outstanding payments by customer</p>
      </div>

      <div className="bg-brick-50 border border-brick-200 rounded-md p-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="font-inter text-sm text-brick-700 mb-1">Total Outstanding Amount</p>
            <p className="font-manrope font-bold text-4xl text-brick-600 tabular-nums">₹{totalPending.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="font-inter text-sm text-brick-700 mb-1">Customers with Dues</p>
            <p className="font-manrope font-bold text-4xl text-brick-600 tabular-nums">{totalCustomers}</p>
          </div>
        </div>
      </div>

      <div data-testid="pending-payments-list" className="space-y-4">
        {customerPayments.length === 0 ? (
          <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-12 text-center">
            <p className="font-inter text-concrete-500">No pending payments</p>
          </div>
        ) : (
          customerPayments.map((customer) => (
            <div key={customer.customer_id} className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
              {/* Customer Header - Always visible */}
              <div 
                className="p-4 cursor-pointer hover:bg-concrete-50 transition-colors"
                onClick={() => toggleExpand(customer.customer_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-brick-100 rounded-full">
                      <User className="h-6 w-6 text-brick-600" />
                    </div>
                    <div>
                      <h3 className="font-manrope font-bold text-lg text-concrete-900">{customer.customer_name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-concrete-600">
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {customer.customer_phone}
                        </span>
                        {customer.customer_address && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {customer.customer_address}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-concrete-500 uppercase tracking-wider">Total Sales</p>
                      <p className="font-semibold text-concrete-700 tabular-nums">₹{customer.total_sales.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-concrete-500 uppercase tracking-wider">Paid</p>
                      <p className="font-semibold text-green-600 tabular-nums">₹{customer.total_paid.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-concrete-500 uppercase tracking-wider">Balance Due</p>
                      <p className="font-bold text-xl text-red-600 tabular-nums">₹{customer.balance_due.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(customer);
                          setPaymentAmount(customer.balance_due);
                          setShowPaymentDialog(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Collect Payment
                      </Button>
                      <Button variant="ghost" size="icon">
                        {expandedCustomer === customer.customer_id ? 
                          <ChevronUp className="h-5 w-5" /> : 
                          <ChevronDown className="h-5 w-5" />
                        }
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Bill Details */}
              {expandedCustomer === customer.customer_id && (
                <div className="border-t border-concrete-200 bg-concrete-50">
                  <div className="p-4">
                    <h4 className="font-manrope font-semibold text-sm text-concrete-700 mb-3">
                      Bill Details ({customer.bills_count} {customer.bills_count === 1 ? 'bill' : 'bills'})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-concrete-100">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-concrete-600">Date</th>
                            <th className="px-4 py-2 text-right font-semibold text-concrete-600">Fly Ash</th>
                            <th className="px-4 py-2 text-right font-semibold text-concrete-600">6" Interlock</th>
                            <th className="px-4 py-2 text-right font-semibold text-concrete-600">8" Interlock</th>
                            <th className="px-4 py-2 text-right font-semibold text-concrete-600">Bill Amount</th>
                            <th className="px-4 py-2 text-right font-semibold text-concrete-600">Paid</th>
                            <th className="px-4 py-2 text-right font-semibold text-concrete-600">Due</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-concrete-200">
                          {customer.bills.map((bill) => (
                            <tr key={bill.sale_id} className="bg-white">
                              <td className="px-4 py-3 text-concrete-700">{bill.date}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-concrete-900">{bill.fly_ash}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-concrete-900">{bill.interlock_6}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-concrete-900">{bill.interlock_8}</td>
                              <td className="px-4 py-3 text-right tabular-nums font-semibold text-concrete-900">₹{bill.total_amount.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-green-600">₹{bill.amount_paid.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right tabular-nums font-semibold text-red-600">₹{bill.amount_due.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Payment Collection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => {
        setShowPaymentDialog(open);
        if (!open) {
          setSelectedCustomer(null);
          setPaymentAmount(0);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              Collect Payment
            </DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="bg-concrete-50 rounded-md p-4">
                <div className="flex items-center gap-3 mb-3">
                  <User className="h-5 w-5 text-concrete-600" />
                  <span className="font-semibold text-concrete-900">{selectedCustomer.customer_name}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-concrete-500">Total Sales</p>
                    <p className="font-semibold tabular-nums">₹{selectedCustomer.total_sales.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-concrete-500">Paid</p>
                    <p className="font-semibold text-green-600 tabular-nums">₹{selectedCustomer.total_paid.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-concrete-500">Balance Due</p>
                    <p className="font-bold text-red-600 tabular-nums">₹{selectedCustomer.balance_due.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                <strong>Note:</strong> Payment will be automatically applied to oldest bills first.
              </div>

              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Payment Amount (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="payment-amount"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                    className="mt-2 tabular-nums text-lg font-semibold"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" disabled={paymentAmount <= 0}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Collect ₹{paymentAmount.toLocaleString()}
                  </Button>
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setShowPaymentDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
